import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

import useHandleRedirect from '../../Hooks/useHandleRedirect';
import useDisableScrolling from '../../Hooks/useDisableScrolling';
import EventJoinModal from './event-join-modal-component';
import FavoriteHeart from '../Events/favorite-heart-component';
import MediaComponent from '../Media/media-component';
import { getEventsScreenData } from '../../Public/events-screen-data';
import { formatDateRange, formatTimeRange, formatWeekday, isEventLocationApproved } from '../Events/event-date-utils';
import PrivateLocationBadge from '../Events/private-location-badge-component';

// ─── Icons ──────────────────────────────────────────────────────────────────
const IconCalendar = (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}><path d="M7 3v3M17 3v3M4 8h16M5 6h14a1 1 0 011 1v12a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const IconClock = (p) => (
    <svg viewBox="0 0 24 24" fill="none" {...p}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" /><path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const IconPin = (p) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1112 6a2.5 2.5 0 010 5.5z" /></svg>
);

function MetaRow({ icon, children }) {
    return (
        <span className="flex items-center gap-2 text-white/80 font-bold font-primary text-sm">
            <span className="text-secondary-300 shrink-0">{icon}</span>
            <span className="min-w-0 truncate">{children}</span>
        </span>
    );
}

/**
 * Event preview modal (opened from the calendar), shows a richer summary of
 * the event (cover, date/time/location, description, highlights) with a Join
 * action (quick lead form) and a link to the full event page.
 *
 * iOS-style bottom sheet on mobile, centered on desktop. Portaled to body.
 */
export default function EventPreviewModal({ event, lang = 'he', dir = 'rtl', onClose }) {
    const handleRedirect = useHandleRedirect();
    const isRtl = dir === 'rtl';
    const { calendar: c, postStrings: t, join: j } = getEventsScreenData(lang);
    const [joinOpen, setJoinOpen] = useState(false);
    const [fav, setFav] = useState(false);

    useDisableScrolling(!!event);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!event || typeof document === 'undefined') return null;

    const cover = event.image || event.seo?.image || event.thumbnail;
    const timeLabel = event.allDay ? c.allDay : formatTimeRange(event.startTime, event.endTime, lang);
    const weekday = formatWeekday(event.startDate, lang);
    const description = event.subtitle || event.intro;
    const highlights = (event.highlights || []).slice(0, 3);

    const goToEvent = () => handleRedirect(`/events/${event.slug}`);

    return ReactDOM.createPortal(
        <>
            <motion.div
                className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                onClick={onClose}
                dir={dir}
            >
                <motion.div
                    onClick={(e) => e.stopPropagation()}
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ type: 'spring', damping: 32, stiffness: 320 }}
                    className="relative w-full sm:w-[440px] max-h-[92vh] overflow-y-auto bg-primary-900 rounded-t-[28px] sm:rounded-[28px] border border-secondary-500/30 shadow-2xl shadow-black/60"
                >
                    {/* Drag handle (mobile) */}
                    <div className="sm:hidden absolute top-0 inset-x-0 pt-3 flex justify-center z-20">
                        <span className="h-1.5 w-11 rounded-full bg-white/40" />
                    </div>

                    {/* Close */}
                    <button
                        onClick={onClose}
                        aria-label="close"
                        className="absolute top-3 end-3 z-20 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors"
                    >
                        ✕
                    </button>

                    {/* Cover */}
                    <div className="relative w-full aspect-[16/10] bg-primary-950 overflow-hidden">
                        {cover ? (
                            <MediaComponent src={cover} alt={event.title} useSekeleton={false} objectFit="cover" wrapperClassName="w-full h-full" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-secondary-800 to-primary-950" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-primary-900 via-primary-900/20 to-transparent" />
                        {event.category && (
                            <span className={`absolute bottom-3 ${isRtl ? 'right-4' : 'left-4'} text-[11px] font-bold px-3 py-1 rounded-full bg-secondary-500 text-white`}>
                                {event.category}
                            </span>
                        )}
                        <FavoriteHeart
                            active={fav}
                            onToggle={() => setFav((v) => !v)}
                            size={20}
                            buttonClassName={`absolute bottom-3 ${isRtl ? 'left-4' : 'right-4'} w-9 h-9 rounded-full bg-black/45 backdrop-blur-sm flex items-center justify-center transition-colors hover:bg-black/65`}
                        />
                    </div>

                    {/* Body */}
                    <div className="p-5 sm:p-6 pt-4" dir={dir}>
                        <h3 className="font-secondary text-2xl font-extralight text-white leading-tight">{event.title}</h3>

                        <div className="mt-3 flex flex-col gap-2">
                            <MetaRow icon={<IconCalendar className="w-4 h-4" />}>
                                {weekday && `${weekday}, `}{formatDateRange(event.startDate, event.endDate, lang)}
                            </MetaRow>
                            {timeLabel && <MetaRow icon={<IconClock className="w-4 h-4" />}>{timeLabel}</MetaRow>}
                            {event.location?.name && (
                                isEventLocationApproved(event)
                                    ? <MetaRow icon={<IconPin className="w-4 h-4" />}>{`${event.location.name}${event.location.city ? `, ${event.location.city}` : ''}`}</MetaRow>
                                    : <PrivateLocationBadge lang={lang} variant="card" />
                            )}
                        </div>

                        {description && (
                            <p className="mt-4 text-white/65 leading-relaxed font-primary line-clamp-3">{description}</p>
                        )}

                        {highlights.length > 0 && (
                            <ul className="mt-4 flex flex-col gap-2">
                                {highlights.map((h, i) => (
                                    <li key={i} className="flex items-start gap-2.5 text-white/80 text-sm font-bold font-primary">
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-secondary-400 shrink-0" />
                                        <span>{h}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Actions */}
                        <div className="mt-6 flex flex-col gap-2.5">
                            <button
                                onClick={() => setJoinOpen(true)}
                                className="w-full rounded-full bg-secondary-500 text-white font-bold py-3.5 shadow-lg shadow-secondary-500/25 hover:bg-secondary-600 transition-colors font-primary"
                            >
                                {t.register}
                            </button>
                            <button
                                onClick={goToEvent}
                                className="w-full rounded-full border border-white/15 text-white font-bold py-3 hover:bg-white/5 transition-colors font-primary"
                            >
                                {j.viewDetails}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            <AnimatePresence>
                {joinOpen && (
                    <EventJoinModal event={event} lang={lang} dir={dir} onClose={() => setJoinOpen(false)} />
                )}
            </AnimatePresence>
        </>,
        document.body
    );
}
